import { inject } from '@angular/core';
import type { Route, Routes } from '@angular/router';

import { LoginComponent } from '@app/auth/components/login/login.component';
import { authGuard } from '@app/auth/guards/auth.guard';
import { projectPermissionLoader } from '@app/auth/guards/project.guard';
import { redirectToHomeGuard } from '@app/auth/guards/redirect-to-home.guard';
import { ForbiddenComponent } from '@app/shared/components/forbidden/forbidden.component';
import { NotFoundComponent } from '@app/shared/components/not-found/not-found.component';
import { SplashComponent } from '@app/shared/components/splash/splash.component';
import {
    MODULE_DISPLAY_NAME,
    MODULE_ROUTE_PATH,
    MODULES,
    PAGE_ROUTE_PATH,
} from '@app/shared/constants/shared.constant';
import { BreadcrumbService } from '@app/shared/services/breadcrumb.service';
import { type ModuleRouteData, RouterFactory } from '@app/shared/utils/router-factory';
import { ChunkNotFoundDialogService } from '@app/theme/services/chunk-not-found-dialog.service';
import { ThemeComponent } from '@app/theme/theme.component';

interface ModuleRoute extends Route {
    path: MODULE_ROUTE_PATH;
    data: ModuleRouteData & { breadcrumb: string };
    canMatch: [typeof authGuard];
    loadChildren: Route['loadChildren'];
}

const projectModuleRoutes: ModuleRoute[] = [
    {
        path: MODULE_ROUTE_PATH.ASSETS,
        loadChildren: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/modules/assets/assets.routes')
                .then(m => m.assetsRoutes)
                .catch(err => RouterFactory.handleChunkNotFound(err, dialogService));
        },
        data: {
            breadcrumb: MODULE_DISPLAY_NAME[MODULES.ASSETS],
            moduleName: MODULES.ASSETS,
        },
        canMatch: [authGuard],
    },
    {
        path: MODULE_ROUTE_PATH.TESTING,
        loadChildren: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/modules/testing/testing.routes')
                .then(m => m.testingRoutes)
                .catch(err => RouterFactory.handleChunkNotFound(err, dialogService));
        },
        data: {
            breadcrumb: MODULE_DISPLAY_NAME[MODULES.TESTING],
            moduleName: MODULES.TESTING,
        },
        canMatch: [authGuard],
    },
    {
        path: MODULE_ROUTE_PATH.MONITOR,
        loadChildren: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/modules/monitor/monitor.routes')
                .then(m => m.monitorRoutes)
                .catch(err => RouterFactory.handleChunkNotFound(err, dialogService));
        },
        data: {
            breadcrumb: MODULE_DISPLAY_NAME[MODULES.MONITOR],
            moduleName: MODULES.MONITOR,
        },
        canMatch: [authGuard],
    },
    {
        path: MODULE_ROUTE_PATH.MANAGEMENT,
        loadChildren: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/modules/management/management.routes')
                .then(m => m.managementRoutes)
                .catch(err => RouterFactory.handleChunkNotFound(err, dialogService));
        },
        data: {
            breadcrumb: MODULE_DISPLAY_NAME[MODULES.MANAGEMENT],
            moduleName: MODULES.MANAGEMENT,
        },
        canMatch: [authGuard],
    },
];

const extraRoutes: Routes = [
    {
        path: MODULE_ROUTE_PATH.HOME,
        loadComponent: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/modules/home-page/home-page.component')
                .then(m => m.HomePageComponent)
                .catch(err => RouterFactory.handleChunkNotFound(err, dialogService));
        },
        data: {
            breadcrumb: 'Home',
        },
        canMatch: [authGuard],
    },
    {
        path: 'ai-chat',
        loadComponent: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/shared/components/ai-chat/ai-chat.component')
                .then(m => m.AIChatComponent)
                .catch(err => RouterFactory.handleChunkNotFound(err, dialogService));
        },
        canActivate: [authGuard],
        data: {
            title: 'CORRA Chat',
            breadcrumb: 'CORRA Chat',
        },
    },
    {
        path: 'unauthorised',
        component: ForbiddenComponent,
        pathMatch: 'full',
        data: {
            title: 'Forbidden',
        },
    },
    {
        path: 'reload',
        component: SplashComponent,
        pathMatch: 'full',
        data: {
            title: 'Reloading...',
        },
    },
    {
        path: 'not-found',
        component: NotFoundComponent,
        pathMatch: 'full',
        data: {
            title: 'Not Found',
        },
    },
];

export const themeRoutes: Routes = [
    {
        path: MODULE_ROUTE_PATH.SETTING,
        outlet: 'settings',
        loadChildren: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/modules/settings/settings-routes')
                .then(m => m.settingsRoutes)
                .catch(err => RouterFactory.handleChunkNotFound(err, dialogService));
        },
        canMatch: [authGuard],
    },
    {
        path: '',
        component: ThemeComponent,
        canMatch: [authGuard],
        children: [
            { path: '', redirectTo: MODULE_ROUTE_PATH.HOME, pathMatch: 'full' },
            ...projectModuleRoutes,
            ...extraRoutes,
        ],
    },
    {
        path: ':projectSlug',
        component: ThemeComponent,
        canMatch: [authGuard, projectPermissionLoader],
        resolve: { customBreadcrumbs: BreadcrumbService },
        children: [
            {
                path: '',
                loadComponent: () => {
                    const dialogService = inject(ChunkNotFoundDialogService);
                    return import('@app/modules/project-home-page/project-home-page.component')
                        .then(m => m.ProjectHomePageComponent)
                        .catch(err => RouterFactory.handleChunkNotFound(err, dialogService));
                },
                canMatch: [authGuard],
                pathMatch: 'full',
            },
            ...projectModuleRoutes,
            {
                path: PAGE_ROUTE_PATH.PROJECT_SETTINGS,
                loadChildren: () => {
                    const dialogService = inject(ChunkNotFoundDialogService);
                    return import('@app/modules/project-settings/project-settings.route')
                        .then(m => m.projectSettingsRoutes)
                        .catch(err => RouterFactory.handleChunkNotFound(err, dialogService));
                },
                canMatch: [authGuard],
            },
            {
                path: 'unauthorised',
                component: ForbiddenComponent,
                pathMatch: 'full',
                data: {
                    title: 'Forbidden',
                },
            },
        ],
    },
    {
        /* This route will be activated when no route matches in all the routing modules and
           redirects to home. The component here is never actually used. If:
           - User is not logged in - authGuard will redirect to /login
           - User is logged in - redirectToHomeGuard will redirect to home
        */
        path: '**',
        component: LoginComponent,
        pathMatch: 'full',
        canActivate: [authGuard, redirectToHomeGuard],
    },
];
